import Link from 'next/link'
import { HiOutlineHome, HiOutlineCog } from 'react-icons/hi'
import { BsStars } from 'react-icons/bs'
import { MdOutlineQueryStats, MdOutlineStorefront } from 'react-icons/md'
import { classNames } from 'src/helpers/utilities'
import { API_BASE_URL } from 'src/data/constants'
import type { IconType } from 'react-icons/lib/cjs'

type NavItem = {
  name: string
  href: string
  icon: IconType
  joyrideId?: string
}

const navigation: NavItem[] = [
  { name: 'Home', href: '/', icon: HiOutlineHome },
  { name: 'Mint', href: '/mint', icon: BsStars, joyrideId: 'jr-mint' },
  {
    name: 'Marketplace',
    href: '/browse?state=forSale',
    icon: MdOutlineStorefront,
    joyrideId: 'jr-marketplace'
  },
  { name: 'Analytics', href: '/analytics', icon: MdOutlineQueryStats, joyrideId: 'jr-analytics' },
  { name: 'Manage', href: '/manage', icon: HiOutlineCog, joyrideId: 'jr-manage' }
]

type ResourceItem = {
  name: string
  href: string
}

const network =
  process.env.NEXT_PUBLIC_VERCEL_ENV === 'mainnet' ? '' : `${process.env.NEXT_PUBLIC_VERCEL_ENV}`

const resources: ResourceItem[] = [
  // { name: 'Documentation', href: '#' },
  {
    name: 'Documentation',
    href: 'https://docs.nf.domains'
  },
  { name: 'Video tutorials', href: 'https://www.youtube.com/channel/UCdPzEzZhsc5vqLIRJLDClBQ' },
  {
    name: 'API Docs',
    href: `https://editor.swagger.io/?url=${API_BASE_URL}/info/openapi3.yaml`
  },
  ...(process.env.NEXT_PUBLIC_VERCEL_ENV !== 'mainnet'
    ? [
        {
          name: `${network} Dispenser`,
          href:
            network === 'betanet'
              ? 'https://betanet.algoexplorer.io/dispenser'
              : 'https://bank.testnet.algorand.network/'
        }
      ]
    : [])
]

const community: ResourceItem[] = [
  { name: 'Discord', href: 'https://discord.gg/7XcuMTfeZP' },
  { name: 'Twitter', href: 'https://twitter.com/nfdomains' }
]

interface NavInterface {
  pathname: string
}

type NavProps = (
  | {
      isMobile: boolean
      onClose: () => void
    }
  | {
      isMobile?: never
      onClose?: never
    }
) &
  NavInterface

export default function Nav({ pathname = '/', isMobile = false, onClose }: NavProps) {
  const getIsCurrent = (href: string) => {
    if (href === '/') return pathname === '/'

    // highlight `Browse` nav item when current page is `/name/:id`
    if (pathname.startsWith('/name')) return href.startsWith('/browse')

    // highlight `Browse` nav item when current page is `/offer/:id`
    if (pathname.startsWith('/offer')) return href.startsWith('/browse')

    // highlight `Browse` nav item when current page is `/address/:address`
    if (pathname.startsWith('/address')) return href.startsWith('/browse')

    const trimHref = href.replace(/\?.*$/, '')

    return pathname.startsWith(trimHref)
  }

  const renderNavItem = (item: NavItem) => {
    const { name, href, icon: Icon, joyrideId } = item

    const isCurrent = getIsCurrent(href)

    const linkClasses = classNames(
      isCurrent
        ? 'bg-gradient-to-r from-brand-400 to-brand-600 text-white'
        : 'text-gray-300 hover:bg-gray-700 hover:text-white',
      isMobile ? 'text-base' : 'text-sm',
      `group flex items-center px-2 py-2 font-medium rounded-md outline-white`
    )

    const iconClasses = classNames(
      isCurrent ? 'text-white' : 'text-gray-400 group-hover:text-gray-300',
      'mr-4 flex-shrink-0 h-6 w-6'
    )

    if (isMobile && onClose && pathname.split('?')[0] === href) {
      return (
        <button
          key={name}
          type="button"
          onClick={onClose}
          className={classNames(
            linkClasses,
            'w-full text-left focus:outline-none focus:ring-1 focus:ring-offset-2 focus:ring-offset-white focus:ring-white'
          )}
          aria-current="page"
        >
          <Icon className={iconClasses} aria-hidden="true" />
          {name}
        </button>
      )
    }

    return (
      <Link key={name} href={href}>
        <a
          className={linkClasses}
          aria-current={isCurrent ? 'page' : undefined}
          data-joyride-id={joyrideId}
        >
          <Icon className={iconClasses} aria-hidden="true" />
          {name}
        </a>
      </Link>
    )
  }

  const renderResourceItem = (item: ResourceItem) => {
    const { name, href } = item

    const linkClasses = classNames(
      isMobile ? 'text-base' : 'text-sm',
      'flex items-center text-gray-300 hover:bg-gray-700 hover:text-white px-2 py-2 font-medium rounded-md outline-white'
    )

    return (
      <a key={name} href={href} className={linkClasses} target="_blank" rel="noreferrer">
        <span className="truncate capitalize">{name}</span>
      </a>
    )
  }

  return (
    <>
      <div className={classNames(isMobile ? 'mt-1' : '', 'space-y-1')}>
        {navigation.map((item) => renderNavItem(item))}
      </div>

      <div className="mt-10" data-joyride-id="jr-resources">
        <p className="px-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
          Resources
        </p>
        <div className="mt-2 space-y-1">{resources.map((item) => renderResourceItem(item))}</div>
      </div>

      <div className="hidden lg:block mt-10" data-joyride-id="jr-community">
        <p className="px-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
          Community
        </p>
        <div className="mt-2 space-y-1">{community.map((item) => renderResourceItem(item))}</div>
      </div>
    </>
  )
}
